import { Destructable, DestructionObserver, Subscription } from "../../destructables";
import { ScalarProvider } from "./Scalar";
import { DictionaryProvider, DictionaryConsumer } from "./Dictionary";
import { MutableVariable } from "./Variable";

export class MutableDictionary<KeyType, ValueType>
	extends DictionaryProvider<KeyType, ValueType>
	implements DictionaryConsumer<KeyType, ValueType>, Destructable
{
	private readonly _entries = new Map<KeyType, ValueType>();
	private readonly _subscribers = new Set<DictionaryConsumer<KeyType, ValueType>>();

	constructor(private readonly expression?: DictionaryProvider<KeyType, ValueType>) {
		super();
	}

	add(key: KeyType, value: ValueType) {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		if (this._entries.has(key)) throw new Error("unexpected");
		this._entries.set(key, value);
		for (const subscriber of this._subscribers) {
			subscriber.add(key, value);
		}
		this._size?.ensure(this._entries.size);
	}

	remove(key: KeyType) {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		if (!this._entries.has(key)) throw new Error("unexpected");
		this._entries.delete(key);
		for (const subscriber of this._subscribers) {
			subscriber.remove(key);
		}
		this._size?.ensure(this._entries.size);
	}

	subscribe(subscriber: DictionaryConsumer<KeyType, ValueType>) {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		if (this._subscribers.has(subscriber)) throw new Error("unexpected");
		this._subscribers.add(subscriber);
		if (this.expression && this._subscribers.size === 1) {
			this.expression.subscribe(this);
		} else {
			for (const [key, value] of this._entries) {
				subscriber.add(key, value);
			}
		}
		return subscriber;
	}

	unsubscribe(subscriber: DictionaryConsumer<KeyType, ValueType>) {
		if (!this._subscribers.has(subscriber)) throw new Error("unexpected");
		this._subscribers.delete(subscriber);
		if (this.expression && this._subscribers.size === 0) {
			this.expression.unsubscribe(this);
			this._entries.clear();
		}
	}

	get entries(): ReadonlyMap<KeyType, ValueType> {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		if (this.expression && this._subscribers.size === 0) {
			return this.expression.entries;
		}
		return this._entries;
	}

	private _size: MutableVariable<number> | undefined;
	get size(): ScalarProvider<number> {
		if (this._destroyed) throw new Error("Trying to access destroyed variable");
		return (this._size ??= new MutableVariable(this._entries.size));
	}

	/* Destructable interface implementation */

	private _destruction_observers?: Set<DestructionObserver>;
	private _destroyed = false;

	destroy(): void {
		if (this._destroyed) return;
		this._destroyed = true;

		if (this._subscribers.size > 0) {
			this._subscribers.clear();
			this.expression?.unsubscribe(this);
		}

		if (this._destruction_observers) {
			for (const observer of this._destruction_observers) {
				observer();
			}
			this._destruction_observers.clear();
		}
	}

	get destroyed(): boolean {
		return this._destroyed;
	}

	onDestroy(observer: DestructionObserver): Subscription {
		if (this.destroyed) {
			observer();
		} else {
			(this._destruction_observers ??= new Set()).add(observer);
		}
		return {
			unsubscribe: () => this._destruction_observers!.delete(observer),
		};
	}
}

type ReadonlyDictionary<KeyType, ValueType> = Omit<MutableDictionary<KeyType, ValueType>, "add" | "remove">;

export function createDictionary<KeyType, ValueType>(): MutableDictionary<KeyType, ValueType>;
export function createDictionary<KeyType, ValueType>(
	expression: DictionaryProvider<KeyType, ValueType>
): ReadonlyDictionary<KeyType, ValueType>;
export function createDictionary<KeyType, ValueType>(
	expression?: DictionaryProvider<KeyType, ValueType>
): DictionaryProvider<KeyType, ValueType> {
	return new MutableDictionary(expression);
}
